import React, { useMemo, useState } from 'react';
import { useBookingApi } from './bookingApi';
import { DAY_KEYS, DAY_LABELS, DAY_SHORT } from './bookingFormat';

/**
 * Weekly recurring-availability editor for a single resource. Authors the
 * `{ weekly: { mon: [{start,end}], … } }` pattern in the resource's timezone,
 * exactly what PUT /resources/:id/availability expects. Semi-independent: it
 * fetches nothing on its own beyond the passed resource and reports the saved
 * resource back via onSaved.
 */
function normalizeWeekly(recurring) {
    const weekly = (recurring && recurring.weekly) || {};
    const out = {};
    DAY_KEYS.forEach(day => {
        const ranges = Array.isArray(weekly[day]) ? weekly[day] : [];
        out[day] = ranges
            .filter(r => r && r.start && r.end)
            .map(r => ({ start: r.start, end: r.end }));
    });
    return out;
}

export default function AvailabilityEditor({ resource, onSaved }) {
    const api = useBookingApi();
    const [weekly, setWeekly] = useState(() => normalizeWeekly(resource.recurringAvailability));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);

    const isOpenDay = day => weekly[day] && weekly[day].length > 0;

    const mutate = (updater) => {
        setSaved(false);
        setWeekly(prev => updater({ ...prev }));
    };

    const toggleDay = (day) => {
        mutate(next => {
            next[day] = isOpenDay(day) ? [] : [{ start: '09:00', end: '17:00' }];
            return next;
        });
    };

    const addRange = (day) => {
        mutate(next => {
            const last = next[day][next[day].length - 1];
            next[day] = [...next[day], { start: last?.end || '09:00', end: '17:00' }];
            return next;
        });
    };

    const removeRange = (day, idx) => {
        mutate(next => {
            next[day] = next[day].filter((_, i) => i !== idx);
            return next;
        });
    };

    const setRange = (day, idx, field, value) => {
        mutate(next => {
            next[day] = next[day].map((range, i) => (i === idx ? { ...range, [field]: value } : range));
            return next;
        });
    };

    const copyToAll = (day) => {
        mutate(next => {
            const template = next[day].map(r => ({ ...r }));
            DAY_KEYS.forEach(d => { next[d] = template.map(r => ({ ...r })); });
            return next;
        });
    };

    const validationError = useMemo(() => {
        for (const day of DAY_KEYS) {
            for (const range of weekly[day]) {
                if (!range.start || !range.end) return `${DAY_LABELS[day]}: fill in both start and end times.`;
                if (range.start >= range.end) return `${DAY_LABELS[day]}: end time must be after start time.`;
            }
        }
        return null;
    }, [weekly]);

    const handleSave = async () => {
        if (validationError) { setError(validationError); return; }
        setError(null);
        setSaving(true);
        try {
            const updated = await api.saveAvailability(resource.id, {
                recurringAvailability: { weekly },
            });
            setSaved(true);
            if (onSaved) onSaved(updated);
        } catch (err) {
            setError(err.message || 'Could not save availability.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="booking-availability-editor">
            <p className="help">
                Weekly hours are authored in this calendar's timezone
                (<strong>{resource.timezone}</strong>). Leave a day off to mark it closed.
            </p>

            {error && <div className="error booking-inline-error">{error}</div>}

            <ul className="booking-week">
                {DAY_KEYS.map(day => (
                    <li key={day} className={`booking-day${isOpenDay(day) ? ' is-open' : ''}`}>
                        <div className="booking-day-head">
                            <label className="checkbox" htmlFor={`bk-day-${resource.id}-${day}`}>
                                <input
                                    id={`bk-day-${resource.id}-${day}`}
                                    type="checkbox"
                                    checked={isOpenDay(day)}
                                    onChange={() => toggleDay(day)}
                                />
                                <span>
                                    <span className="booking-day-full">{DAY_LABELS[day]}</span>
                                    <span className="booking-day-short">{DAY_SHORT[day]}</span>
                                </span>
                            </label>
                            {isOpenDay(day) && (
                                <button
                                    type="button"
                                    className="booking-linkbtn"
                                    onClick={() => copyToAll(day)}
                                    title="Copy these hours to every day"
                                >
                                    Copy to all
                                </button>
                            )}
                        </div>

                        {isOpenDay(day) ? (
                            <div className="booking-day-ranges">
                                {weekly[day].map((range, idx) => (
                                    <div key={idx} className="booking-range">
                                        <input
                                            type="time"
                                            value={range.start}
                                            onChange={e => setRange(day, idx, 'start', e.target.value)}
                                            aria-label={`${DAY_LABELS[day]} start`}
                                        />
                                        <span aria-hidden="true">–</span>
                                        <input
                                            type="time"
                                            value={range.end}
                                            onChange={e => setRange(day, idx, 'end', e.target.value)}
                                            aria-label={`${DAY_LABELS[day]} end`}
                                        />
                                        <button
                                            type="button"
                                            className="booking-iconbtn"
                                            onClick={() => removeRange(day, idx)}
                                            aria-label="Remove this time range"
                                        >
                                            <i className="fas fa-times" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                ))}
                                <button type="button" className="booking-linkbtn" onClick={() => addRange(day)}>
                                    <i className="fas fa-plus" aria-hidden="true"></i> Add hours
                                </button>
                            </div>
                        ) : (
                            <span className="booking-day-closed">Closed</span>
                        )}
                    </li>
                ))}
            </ul>

            <div className="booking-form-actions">
                {saved && <span className="booking-saved-hint"><i className="fas fa-check" aria-hidden="true"></i> Saved</span>}
                <button type="button" className="button primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save weekly hours'}
                </button>
            </div>
        </div>
    );
}
